import { useState, useEffect } from "react";
import { getDocs, collection, query, orderBy, where } from "firebase/firestore";
import { FaStar, FaRegStar } from "react-icons/fa";
import { Link } from "./usePath";

const Ranking = ({ db, filters = {} }) => {
  const [videos, setVideos] = useState([]);

  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  useEffect(() => {
    const fetchRankedVideos = async () => {
      try {
        let filteredVideos = [];

        // First, determine if we need to use post-filtering
        const needsPostFiltering =
          (filters.winner && filters.winner.length > 0) ||
          (filters.award && filters.award.length > 0) ||
          (filters.elo && filters.elo.length > 0);

        // If we don't need post-filtering, we can use the regular query approach
        if (!needsPostFiltering) {
          let videosQuery = query(
            collection(db, "videos"),
            orderBy("elo", "desc")
          );

          const filterConditions = [];

          if (filters.division && filters.division.length > 0) {
            filterConditions.push(where("division", "in", filters.division));
          }

          if (filters.year && filters.year.length > 0) {
            filterConditions.push(where("year", "in", filters.year));
          }

          if (filters.school && filters.school.length > 0) {
            filterConditions.push(where("school", "in", filters.school));
          }

          if (filterConditions.length > 0) {
            videosQuery = query(
              collection(db, "videos"),
              ...filterConditions,
              orderBy("elo", "desc")
            );
          }

          const snapshot = await getDocs(videosQuery);
          filteredVideos = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
        } else {
          // We need to fetch all and filter manually because of complex filtering logic
          let videosQuery = query(
            collection(db, "videos"),
            orderBy("elo", "desc")
          );

          const snapshot = await getDocs(videosQuery);
          let allVideos = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          // Apply filters
          filteredVideos = allVideos.filter((video) => {
            // Apply division filter
            if (filters.division && filters.division.length > 0) {
              if (!filters.division.includes(video.division)) {
                return false;
              }
            }

            // Apply year filter
            if (filters.year && filters.year.length > 0) {
              if (!filters.year.includes(video.year)) {
                return false;
              }
            }

            // Apply school filter
            if (filters.school && filters.school.length > 0) {
              if (!filters.school.includes(video.school)) {
                return false;
              }
            }

            // Apply winner/finalist filter
            if (filters.winner && filters.winner.length > 0) {
              if (filters.winner.includes("Winner") && video.winner) {
                // Keep winners
              } else if (
                filters.winner.includes("Finalist") &&
                video.finalist
              ) {
                // Keep finalists
              } else if (
                filters.winner.includes("Nominee") &&
                !video.winner &&
                !video.finalist
              ) {
                // Keep Nominee (not winners or finalists)
              } else {
                return false;
              }
            }

            // Apply award filter
            if (filters.award && filters.award.length > 0) {
              if (!filters.award.includes(video.award)) {
                return false;
              }
            }

            // Apply Elo filter
            if (filters.elo && filters.elo.length > 0) {
              const threshold = parseInt(filters.elo[0]);
              if (video.elo < threshold) {
                return false;
              }
            }

            return true;
          });
        }

        setVideos(filteredVideos);
      } catch (error) {
        console.error("Error fetching ranked videos:", error);
      }
    };

    fetchRankedVideos();
  }, [db, filters]);

  return (
    <div style={{ 
      padding: "30px"
    }}>
      <h2>Community Rankings</h2>
      {videos.length === 0 ? (
        <p>No videos match the current filters.</p>
      ) : (
        <>
          <div style={{ 
            overflowX: "auto", 
            width: "100%",
            borderRadius: "16px"
          }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: "650px",
              }}
            >
              <thead>
                <tr
                  style={{
                    backgroundColor: "#f4f4f4",
                    borderBottom: "2px solid #ddd",
                  }}
                >
                  <th style={tableHeaderStyle}>Rank</th>
                  <th style={tableHeaderStyle}>Div</th>
                  <th style={tableHeaderStyle}>Video</th>
                  <th style={tableHeaderStyle}>School</th>
                  <th style={tableHeaderStyle}>Year</th>
                  <th style={tableHeaderStyle}>Award</th>
                  {/* <th style={tableHeaderStyle}>Votes</th> */}
                  <th style={tableHeaderStyle}>Elo</th>
                </tr>
              </thead>
              <tbody>
                {videos.map((video, index) => (
                  <tr key={video.id} style={tableRowStyle}>
                    <td style={tableCellStyle}>{index + 1}</td>
                    <td style={tableCellStyle}>{video.division || "?"}</td>
                    <td style={tableCellStyle}>
                      <Link
                        to={`/video/${video.id}`}
                        style={{ wordBreak: "break-word" }}
                      >
                        {video.name || "??"}{" "}
                      </Link>
                      {video.winner ? (
                        <FaStar style={{ color: "gold", fontSize: "16px" }} />
                      ) : video.finalist ? (
                        <FaRegStar
                          style={{ color: "grey", fontSize: "12px" }}
                        />
                      ) : null}{" "}
                    </td>
                    <td style={tableCellStyle}>
                      <Link to={`/school/${encodeURIComponent(video.school || "")}`}>
                        {video.school}
                      </Link>
                    </td>
                    <td style={tableCellStyle}>{video.year}</td>
                    <td style={tableCellStyle}>{video.award}</td>
                    {/* <td style={tableCellStyle}>
                      {formatNumber(video.numVotesFor)}
                    </td> */}
                    <td style={tableCellStyle}>{video.elo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <br />
          <a href="https://forms.gle/u8LjUr8zMRGwiVQK7">
            <button className="vote-button">Add video</button>
          </a>
          <a href="https://forms.gle/wxjNgxTSvDr6N3yS9">
            <button className="vote-button">Site feedback</button>
          </a>
        </>
      )}
    </div>
  );
};

const tableHeaderStyle = {
  padding: "10px",
  textAlign: "left",
  fontWeight: "bold",
};

const tableRowStyle = {
  textAlign: "left",
  borderBottom: "1px solid #ddd",
};

const tableCellStyle = {
  padding: "10px",
};

export default Ranking;
